// Import types only to avoid loading the Twilio SDK during SSR.
// The actual SDK is dynamically imported in the browser when needed.
import type { Device as TwilioDevice, Call as TwilioCall } from '@twilio/voice-sdk'
import {
    VoiceService,
    VoiceConnection,
    VoiceDevice,
    VoiceServiceError,
    VoiceServiceEvents,
    VoiceCallOptions,
    VoiceConnectionStatus,
    AudioDevices,
    AudioDevice
} from './types'
import { BaseApiService } from '../BaseApiService'

/**
 * Twilio Voice Service Implementation
 *
 * This class implements the VoiceService interface using Twilio's Voice SDK.
 * It abstracts away Twilio-specific details and provides a clean interface.
 */
export class TwilioVoiceService extends BaseApiService implements VoiceService {
    private device: TwilioDevice | null = null
    private activeCall: TwilioCall | null = null
    private activeConnection: VoiceConnection | null = null
    private events: Partial<VoiceServiceEvents> = {}
    private durationTimer: ReturnType<typeof setInterval> | null = null
    private getNewToken: (() => Promise<string>) | null = null
    private selectedInputDeviceId: string | null = null
    private isDestroying = false

    constructor(getNewToken?: () => Promise<string>) {
        super()
        this.getNewToken = getNewToken || null
    }

    async initialize(accessToken: string, events?: Partial<VoiceServiceEvents>): Promise<VoiceDevice> {
        this.events = events || {}

        try {
            console.log(`Initializing Twilio Device with token: ${accessToken}`)
            // Load Twilio SDK dynamically on the client to avoid SSR import issues
            if (typeof window === 'undefined') {
                throw new Error('Twilio Voice SDK can only be initialized in the browser')
            }

            const tw = await import('@twilio/voice-sdk')

            // Create and setup Twilio Device
            this.device = new tw.Device(accessToken, {
                logLevel: import.meta.env.DEV ? 'debug' : 'error',
                enableImprovedSignalingErrorPrecision: true,

                // Additional Twilio-specific options can be configured here
            })

            // Setup device event listeners (these will call onDeviceReady when the device emits 'ready')
            this.setupDeviceListeners()

            // Register the device (this may trigger a 'ready' event asynchronously)
            await this.device.register()

            // Device is ready — notify listeners with current device info
            const voiceDevice: VoiceDevice = {
                deviceId: this.device?.identity || 'unknown',
                // Compare to literal to avoid referencing SDK enums at module scope
                isReady: this.device?.state === 'registered',
                isRegistered: this.device?.state === 'registered'
            }

            this.events.onDeviceReady?.(voiceDevice)

            return voiceDevice
        } catch (error) {
            const serviceError = this.mapTwilioError(error)
            this.events.onDeviceError?.(serviceError)
            throw serviceError
        }
    }

    async makeCall(options: VoiceCallOptions): Promise<VoiceConnection> {
        if (!this.device || !this.isReady()) {
            throw new Error('Voice service not ready')
        }

        if (this.activeCall) {
            throw new Error('Another call is already in progress')
        }

        try {
            const params: Record<string, string> = {
                To: options.phoneNumber,
            }

            // Include call token for account resolution on Twilio side
            const callToken = this.device.token;
            if (callToken != null) {
                params.CallToken = callToken;
            }

            // Forward caller ID mode and number to the TwiML webhook
            if (options.callerIdMode) {
                params.CallerIdMode = options.callerIdMode;
            }
            if (options.callerIdNumber) {
                params.CallerIdNumber = options.callerIdNumber;
            }

            // Apply the user's chosen input device before connecting to avoid
            // holding the mic open between calls (unset after disconnect).
            if (this.selectedInputDeviceId && this.device.audio) {
                await this.device.audio.setInputDevice(this.selectedInputDeviceId)
            }

            this.activeCall = await this.device.connect({ params })

            const connection: VoiceConnection = {
                connectionId: this.activeCall.parameters.CallSid || 'unknown',
                phoneNumber: options.phoneNumber,
                status: this.mapTwilioCallStatus(this.activeCall.status()),
                startTime: new Date()
            }

            this.activeConnection = connection
            this.setupCallListeners()

            this.events.onCallStarted?.(connection)

            return connection
        } catch (error) {
            const serviceError = this.mapTwilioError(error)
            this.events.onCallFailed?.(this.activeConnection!, serviceError)
            throw serviceError
        }
    }

    async endCall(): Promise<void> {
        if (!this.activeCall) {
            throw new Error('No active call to end')
        }

        try {
            this.activeCall.disconnect()
            this.activeCall = null
            this.activeConnection = null
        } catch (error) {
            const serviceError = this.mapTwilioError(error)
            this.events.onCallFailed?.(this.activeConnection!, serviceError)
            throw serviceError
        }
    }

    async sendDTMF(digit: string): Promise<void> {
        if (!this.activeCall) {
            throw new Error('No active call to send DTMF')
        }

        try {
            this.activeCall.sendDigits(digit)
        } catch (error) {
            const serviceError = this.mapTwilioError(error)
            throw serviceError
        }
    }

    getConnectionStatus(): VoiceConnectionStatus {
        if (!this.activeConnection) {
            return 'idle'
        }
        return this.activeConnection.status
    }

    getActiveConnection(): VoiceConnection | null {
        return this.activeConnection
    }

    isReady(): boolean {
        return this.device?.state === 'registered'
    }

    getAudioDevices(): AudioDevices {
        if (!this.device) {
            return { inputs: [], outputs: [], selectedInput: null, selectedOutput: null }
        }

        const inputs: AudioDevice[] = []
        const outputs: AudioDevice[] = []

        // Get input devices (microphones)
        this.device.audio?.availableInputDevices.forEach((device, deviceId) => {
            inputs.push({ deviceId, label: device.label || `Microphone ${inputs.length + 1}` })
        })

        // Get output devices (speakers)
        this.device.audio?.availableOutputDevices.forEach((device, deviceId) => {
            outputs.push({ deviceId, label: device.label || `Speaker ${outputs.length + 1}` })
        })

        // Use the stored preference so it persists after unsetInputDevice
        const selectedInput = this.selectedInputDeviceId || this.device.audio?.inputDevice?.deviceId || null
        const selectedOutput = this.device.audio?.speakerDevices.get().values().next().value?.deviceId || null

        return { inputs, outputs, selectedInput, selectedOutput }
    }

    async setInputDevice(deviceId: string): Promise<void> {
        this.selectedInputDeviceId = deviceId
        // Only open the stream if a call is already active. Outside of a call,
        // just saving the preference is enough — makeCall() applies it before
        // connect() and handleCallEnded() releases it via unsetInputDevice().
        if (this.activeCall && this.device?.audio) {
            await this.device.audio.setInputDevice(deviceId)
        }
    }

    async setOutputDevice(deviceId: string): Promise<void> {
        if (!this.device?.audio) {
            throw new Error('Audio not available')
        }
        await this.device.audio.speakerDevices.set(deviceId)
    }

    setMuted(muted: boolean): void {
        if (this.activeCall) {
            this.activeCall.mute(muted)
        }
    }

    isMuted(): boolean {
        return this.activeCall?.isMuted() ?? false
    }

    updateToken(accessToken: string): void {
        if (!this.device) {
            throw new Error('Voice service not initialized')
        }

        try {
            console.log('Updating Twilio Device token')
            this.device.updateToken(accessToken)
        } catch (error) {
            const serviceError = this.mapTwilioError(error)
            this.events.onDeviceError?.(serviceError)
            throw serviceError
        }
    }

    private async refreshTwilioToken(): Promise<void> {
        if (!this.getNewToken) {
            console.warn('No token refresh callback provided, cannot auto-refresh token')
            return
        }

        try {
            console.log('Refreshing Twilio access token...')
            const newToken = await this.getNewToken()
            this.updateToken(newToken)
            console.log('Token refreshed successfully')
        } catch (error) {
            console.error('Failed to refresh token:', error)
            throw error
        }
    }

    destroy(): void {
        this.isDestroying = true
        try {
            // Stop duration tracking
            this.stopDurationTracking()

            if (this.activeCall) {
                this.activeCall.disconnect()
            }

            if (this.device) {
                this.device.destroy()
            }
        } catch (error) {
            console.error('Error destroying voice service:', error)
        } finally {
            this.device = null
            this.activeCall = null
            this.activeConnection = null
            this.isDestroying = false
        }
    }

    private setupDeviceListeners(): void {
        if (!this.device) return

        this.device.on('registered', () => {
            console.log('Twilio Device registered')
            const voiceDevice: VoiceDevice = {
                deviceId: this.device?.identity || 'unknown',
                isReady: true,
                isRegistered: true
            }
            this.events.onDeviceRegistered?.(voiceDevice)
            // Notify after registration so device list is up to date
            this.events.onAudioDevicesChanged?.()
        })

        // Update device list when a headset is plugged in or out
        this.device.audio?.on('deviceChange', () => {
            this.events.onAudioDevicesChanged?.()
        })

        this.device.on('unregistered', () => {
            console.log('Twilio Device unregistered - attempting re-registration')
            this.events.onDeviceUnregistered?.()
            // Skip re-registration if we're intentionally destroying the service
            if (this.isDestroying) {
                console.log('Skipping re-registration: service is being destroyed')
                return
            }
            // Attempt to re-register with a fresh token
            this.refreshTwilioToken()
                .then(() => this.device?.register())
                .catch(error => {
                    console.error('Failed to re-register device:', error)
                    this.events.onDeviceError?.(this.mapTwilioError(error))
                })
        })

        this.device.on('error', (error) => {
            const serviceError = this.mapTwilioError(error)
            this.events.onDeviceError?.(serviceError)
        })

        // Handle token expiration events
        this.device.on('tokenWillExpire', () => {
            console.log('Twilio token will expire soon')
            this.events.onTokenWillExpire?.()
            this.refreshTwilioToken().catch(error => {
                console.error('Failed to refresh token:', error)
            })
        })

        this.device.on('tokenExpired', () => {
            console.log('Twilio token expired')
            this.events.onTokenExpired?.()
            this.refreshTwilioToken().catch(error => {
                console.error('Failed to refresh token after expiration:', error)
            })
        })

        // this.device.on('incoming', (call) => {
        //     console.log('Incoming call received:', call)
        //     // Handle incoming calls if needed in the future
        // })
    }

    private setupCallListeners(): void {
        if (!this.activeCall || !this.activeConnection) return

        this.activeCall.on('accept', () => {
            if (this.activeConnection) {
                this.activeConnection.status = 'connected'
                this.activeConnection.startTime = new Date()
                this.events.onConnectionStatusChange?.(this.activeConnection)

                // Start duration tracking
                this.startDurationTracking()
            }
        })

        this.activeCall.on('disconnect', () => {
            this.handleCallEnded()
        })

        this.activeCall.on('cancel', () => {
            this.handleCallEnded()
        })

        this.activeCall.on('reject', () => {
            this.handleCallEnded()
        })

        this.activeCall.on('error', (error) => {
            // Stop duration tracking on error
            this.stopDurationTracking()

            const serviceError = this.mapTwilioError(error)
            if (this.activeConnection) {
                this.activeConnection.status = 'failed'
                this.events.onCallFailed?.(this.activeConnection, serviceError)
            }
        })

        this.activeCall.on('ringing', (_hasEarlyMedia: boolean) => {
            if (this.activeConnection) {
                this.activeConnection.status = 'ringing'
                this.events.onConnectionStatusChange?.(this.activeConnection)
            }
        })

        this.activeCall.on('reconnecting', (error: unknown) => {
            if (this.activeConnection) {
                this.activeConnection.status = 'reconnecting'
                this.events.onConnectionStatusChange?.(this.activeConnection)
            }
            this.events.onReconnecting?.(this.mapTwilioError(error))
        })

        this.activeCall.on('reconnected', () => {
            if (this.activeConnection) {
                this.activeConnection.status = 'connected'
                this.events.onConnectionStatusChange?.(this.activeConnection)
            }
            this.events.onReconnected?.()
        })

        this.activeCall.on('mute', (_isMuted: boolean) => {
            // Sync mute state from SDK (handles remote mute/unmute)
            this.events.onConnectionStatusChange?.({
                ...this.activeConnection!,
                status: this.activeConnection?.status || 'connected'
            })
        })

        this.activeCall.on('warning', (warningName: string, warningData?: unknown) => {
            console.warn(`Twilio call warning: ${warningName}`, warningData)
            this.events.onWarning?.(warningName, warningData)
        })

        this.activeCall.on('warning-cleared', (warningName: string) => {
            console.log(`Twilio call warning cleared: ${warningName}`)
            this.events.onWarningCleared?.(warningName)
        })
    }

    private handleCallEnded(): void {
        // Stop duration tracking
        this.stopDurationTracking()

        // Release the input device so the browser removes the recording indicator.
        // The selectedInputDeviceId preference is preserved and re-applied on the next call.
        this.device?.audio?.unsetInputDevice().catch(console.error)

        if (this.activeConnection) {
            this.activeConnection.status = 'disconnected'
            this.activeConnection.endTime = new Date()

            if (this.activeConnection.startTime) {
                this.activeConnection.duration = Math.floor(
                    (this.activeConnection.endTime.getTime() - this.activeConnection.startTime.getTime()) / 1000
                )
            }

            this.events.onCallEnded?.(this.activeConnection)
        }

        this.activeCall = null
        this.activeConnection = null
    }

    private startDurationTracking(): void {
        // Clear any existing timer
        this.stopDurationTracking()

        // Update duration every second
        this.durationTimer = setInterval(() => {
            if (!this.activeConnection || this.activeConnection.status !== 'connected') {
                this.stopDurationTracking()
                return
            }

            if (this.activeConnection.startTime) {
                const duration = Math.floor(
                    (new Date().getTime() - this.activeConnection.startTime.getTime()) / 1000
                )

                // Update the connection with the new duration
                this.activeConnection = {
                    ...this.activeConnection,
                    duration
                }

                // Notify listeners of the updated connection
                this.events.onConnectionStatusChange?.(this.activeConnection)
            }
        }, 1000)
    }

    private stopDurationTracking(): void {
        if (this.durationTimer) {
            clearInterval(this.durationTimer)
            this.durationTimer = null
        }
    }

    private mapTwilioCallStatus(twilioStatus: string): VoiceConnectionStatus {
        switch (twilioStatus) {
            case 'pending':
                return 'connecting'
            case 'connecting':
                return 'connecting'
            case 'ringing':
                return 'ringing'
            case 'open':
                return 'connected'
            case 'reconnecting':
                return 'reconnecting'
            case 'closed':
                return 'disconnected'
            default:
                return 'idle'
        }
    }

    private mapTwilioError(error: unknown): VoiceServiceError {
        if (error && typeof error === 'object' && 'code' in error) {
            const code = Number((error as any).code)
            let message = (error as any).message || 'An unknown error occurred'

            // Microphone access denied
            if (code === 31208 || code === 31201) {
                message = 'Microphone access denied. Please allow microphone access and try again.'
            // Token errors (20101–20107)
            } else if (code >= 20101 && code <= 20107) {
                message = 'Access token error. Please refresh the page to reconnect.'
            // Connection errors
            } else if (code === 31003 || code === 31005) {
                message = 'Connection to Twilio lost. Attempting to reconnect...'
            }

            return {
                code: String((error as any).code) || 'UNKNOWN_ERROR',
                message,
                originalError: error
            }
        }

        return {
            code: 'UNKNOWN_ERROR',
            message: error instanceof Error ? error.message : 'An unknown error occurred',
            originalError: error
        }
    }
}
